import { COMPACT_SHOP } from '@/config/touch';
import { ECONOMY } from '@/config/economy';
import { MAX_ACTIVE_WEAPONS } from '@/config/weapons';
import { isCoarsePointer } from '@/input/active-device';
import type { GainAnchors } from './shop-gains';
import { bagWeapons, socketFocusable, type FocusItem, type ShopPage, type ShopState } from './shop-types';

/*
 * COMPACT (phone) UPGRADE BAY geometry (polish 1, docs/06). Its own design
 * space, sized so 1 design px ≈ 1 CSS px on a landscape phone (667×375 →
 * ×1.04, 844×390 → ×1.15): text ≥ 13 px, touch targets ≥ 48 px. Three
 * glyph tabs — CARDS | HANGAR | STATUS — over one page, a wordless footer:
 * the focused item's action + the highlighted CONTINUE. Pure math.
 */

export const COMPACT_W = 640;
export const COMPACT_H = 340;

export const PAGES: ReadonlyArray<ShopPage> = ['cards', 'hangar', 'status'];
export const TAB = { w: 66, h: 48, gap: 6 } as const;
/** Page area (everything below the header, above the footer) */
export const MAIN_Y = 56;
export const MAIN_H = 224;
export const FOOT_Y = 288;
export const FOOT_H = 52;
export const CONTINUE_BTN = { x: 430, w: 210 } as const;
export const MENU_BTN = { x: 592, size: 48 } as const;

// ---- CARDS page (page-local)
export const CARD_C = { w: 166, h: 224, gap: 10, faceH: 176 } as const;
export const REROLL_TILE = { x: 528, w: 112 } as const;

// ---- HANGAR page (page-local)
export const SOCKET_C = { w: 92, h: 120, gap: 6 } as const;
export const COMPARE_C = { y: 128, w: 288, h: 96 } as const;
export const BAG_C = { x: 300, y: 26, w: 84, h: 94, gap: 8, rowGap: 6, cols: 3 } as const;
export const CRATE_C = { x: 576, w: 64 } as const;

// ---- STATUS page (page-local)
export const VITAL_TILE = { w: 196, h: 64, gap: 8 } as const;
export const NEXT_Y = 144;
export const CHIPS_C = { x: 204, y: 20, pitchX: 50, pitchY: 46, cols: 4, rows: 4 } as const;
export const STATS_C = { x: 412, w: 228 } as const;
export const MAX_CHIPS_C = CHIPS_C.cols * CHIPS_C.rows;
/** "+N" blooms rise from the header readouts (shop-compact.ts) */
export const GAIN_ANCHORS_C: GainAnchors = {
  hp: { x: 256, y: TAB.h / 2 }, armor: { x: 352, y: TAB.h / 2 }, gems: { x: 480, y: TAB.h / 2 },
};

/** Phone-sized canvas (short side) or a small touch-first screen. */
export function isCompactShop(screenW: number, screenH: number): boolean {
  const short = Math.min(screenW, screenH);
  return short < COMPACT_SHOP.maxShortSide
    || (short < COMPACT_SHOP.maxShortSideTouch && isCoarsePointer());
}

/** Page holding a focus item (tabs + CONTINUE live on every page). */
export function pageOf(item: FocusItem): ShopPage | null {
  switch (item.kind) {
    case 'card': case 'reroll': return 'cards';
    case 'socket': case 'weapon': case 'bagBuy': return 'hangar';
    case 'heal': case 'buyArmor': case 'chip': return 'status';
    default: return null;
  }
}

/** Focus list of one page: tabs, the page's items, CONTINUE. */
export function compactFocus(s: ShopState, page: ShopPage): FocusItem[] {
  const items: FocusItem[] = PAGES.map((p) => ({ kind: 'tab', page: p }) as FocusItem);
  if (page === 'cards') {
    s.offer.forEach((_, index) => items.push({ kind: 'card', index }));
    items.push({ kind: 'reroll' });
  } else if (page === 'hangar') {
    for (let i = 0; i < MAX_ACTIVE_WEAPONS; i++) {
      if (socketFocusable(s, i)) items.push({ kind: 'socket', index: i });
    }
    for (const weaponId of bagWeapons(s)) items.push({ kind: 'weapon', weaponId });
    if (s.bagPrice !== null) items.push({ kind: 'bagBuy' });
    if (s.pending) items.push({ kind: 'weapon', weaponId: s.pending });
  } else {
    items.push({ kind: 'heal' }, { kind: 'buyArmor' });
    for (const e of s.collection.slice(0, MAX_CHIPS_C)) items.push({ kind: 'chip', cardId: e.card.id });
  }
  items.push({ kind: 'continue' });
  return items;
}

export function tabX(i: number): number {
  return i * (TAB.w + TAB.gap);
}

export function socketX(i: number): number {
  return i * (SOCKET_C.w + SOCKET_C.gap);
}

/** Bag cell i (weapons, empty slots, padlock), page-local top-left. */
export function bagCellPos(i: number): { x: number; y: number } {
  const col = i % BAG_C.cols;
  const row = Math.floor(i / BAG_C.cols);
  return { x: BAG_C.x + col * (BAG_C.w + BAG_C.gap), y: BAG_C.y + row * (BAG_C.h + BAG_C.rowGap) };
}

/** Bag cell of a weapon / the padlock, -1 = the waiting crate. */
export function compactBagCell(item: FocusItem, s: ShopState): number {
  if (item.kind === 'bagBuy') return Math.min(s.bagCap, ECONOMY.inventory.total - 1);
  return item.kind === 'weapon' ? bagWeapons(s).indexOf(item.weaponId) : -1;
}

export function chipCellPos(i: number): { x: number; y: number } {
  const col = i % CHIPS_C.cols;
  const row = Math.floor(i / CHIPS_C.cols);
  return {
    x: CHIPS_C.x + CHIPS_C.pitchX / 2 + col * CHIPS_C.pitchX,
    y: CHIPS_C.y + CHIPS_C.pitchY / 2 + row * CHIPS_C.pitchY,
  };
}

/** Design-space centre of a focus item — spatial nav + equip flights. */
export function compactItemPos(item: FocusItem, s: ShopState): { x: number; y: number } {
  const mid = MAIN_Y + MAIN_H / 2;
  switch (item.kind) {
    case 'tab': return { x: tabX(PAGES.indexOf(item.page)) + TAB.w / 2, y: TAB.h / 2 };
    case 'continue': return { x: CONTINUE_BTN.x + CONTINUE_BTN.w / 2, y: FOOT_Y + FOOT_H / 2 };
    case 'card': return { x: item.index * (CARD_C.w + CARD_C.gap) + CARD_C.w / 2, y: mid };
    case 'reroll': return { x: REROLL_TILE.x + REROLL_TILE.w / 2, y: mid };
    // Sockets share the bag's first row so ←/→ walks sockets → bag
    case 'socket': return { x: socketX(item.index) + SOCKET_C.w / 2, y: MAIN_Y + BAG_C.y + BAG_C.h / 2 };
    case 'weapon':
    case 'bagBuy': {
      const cell = compactBagCell(item, s);
      if (cell < 0) return { x: CRATE_C.x + CRATE_C.w / 2, y: mid };
      const p = bagCellPos(cell);
      return { x: p.x + BAG_C.w / 2, y: MAIN_Y + p.y + BAG_C.h / 2 };
    }
    case 'heal': return { x: VITAL_TILE.w / 2, y: MAIN_Y + VITAL_TILE.h / 2 };
    case 'buyArmor':
      return { x: VITAL_TILE.w / 2, y: MAIN_Y + VITAL_TILE.h + VITAL_TILE.gap + VITAL_TILE.h / 2 };
    case 'chip': {
      const idx = s.collection.slice(0, MAX_CHIPS_C).findIndex((e) => e.card.id === item.cardId);
      const p = chipCellPos(Math.max(0, idx));
      return { x: p.x, y: MAIN_Y + p.y };
    }
  }
}
